import { User } from 'firebase/auth';
import { supabaseClient } from '@/shared/services/client';
import { UserProfile, Service, NotificationPreferences, TemplateId, CurrencyCode, UserRole } from '@/shared/types';
import type { PaymentMethod } from '@/shared/types/business';

interface ProfileMapping {
  firebase_uid: string;
  supabase_id: string;
  created_at: string;
}

/**
 * User Profile Synchronization Service
 * Handles mapping between Firebase user IDs and Supabase UUIDs
 */
class UserProfileSyncService {
  private static instance: UserProfileSyncService;
  private profileCache: Map<string, string> = new Map();

  private constructor() {}

  static getInstance(): UserProfileSyncService {
    if (!UserProfileSyncService.instance) {
      UserProfileSyncService.instance = new UserProfileSyncService();
    }
    return UserProfileSyncService.instance;
  }

  /**
   * Get or create a Supabase profile for a Firebase user
   */
  async getOrCreateSupabaseProfile(firebaseUser: User): Promise<UserProfile> {
    const firebaseUid = firebaseUser.uid;

    // Check cache first
    if (this.profileCache.has(firebaseUid)) {
      const supabaseId = this.profileCache.get(firebaseUid)!;
      const profile = await this.getSupabaseProfile(supabaseId);
      if (profile) {
        return profile;
      }
    }

    // Check if mapping exists
    let supabaseId = await this.getSupabaseIdFromFirebaseUid(firebaseUid);

    if (!supabaseId) {
      // Create new profile with UUID
      supabaseId = await this.createSupabaseProfile(firebaseUser);
    }

    // Cache the mapping
    this.profileCache.set(firebaseUid, supabaseId);

    // Return the complete profile
    const profile = await this.getSupabaseProfile(supabaseId);
    if (!profile) {
      throw new Error('Failed to create or retrieve user profile');
    }

    return profile;
  }

  /**
   * Get Supabase UUID from Firebase UID
   */
  private async getSupabaseIdFromFirebaseUid(firebaseUid: string): Promise<string | null> {
    const client = supabaseClient.getClient();

    try {
      // First check if there's a direct mapping in profile_mappings table
      const { data: mapping } = await client
        .from('profile_mappings')
        .select('supabase_id')
        .eq('firebase_uid', firebaseUid)
        .single();

      if (mapping) {
        return mapping.supabase_id;
      }

      // Note: profiles table doesn't have firebase_uid column
      // All profile mappings should go through profile_mappings table

      return null;
    } catch (error) {
      console.log('Profile mapping not found, will create new one');
      return null;
    }
  }

  /**
   * Create a new Supabase profile for Firebase user
   */
  private async createSupabaseProfile(firebaseUser: User): Promise<string> {
    const client = supabaseClient.getClient();
    const supabaseId = crypto.randomUUID();

    try {
      // Check if a profile with this email already exists to avoid duplicates
      const { data: existingProfile } = await client
        .from('profiles')
        .select('id')
        .eq('email', firebaseUser.email)
        .single();

      if (existingProfile) {
        // Create mapping for existing profile
        await client
          .from('profile_mappings')
          .insert({
            firebase_uid: firebaseUser.uid,
            supabase_id: existingProfile.id,
            created_at: new Date().toISOString()
          });

        console.log('✅ Linked Firebase user to existing profile:', {
          firebaseUid: firebaseUser.uid,
          supabaseId: existingProfile.id,
          email: firebaseUser.email
        });

        return existingProfile.id;
      }

      // Create the profile with proper UUID
      const { error: profileError } = await client
        .from('profiles')
        .insert({
          id: supabaseId,
          email: firebaseUser.email,
          name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Unknown',
          full_name: firebaseUser.displayName,
          avatar_url: firebaseUser.photoURL,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (profileError) {
        // Check if it's a duplicate entry error
        if (profileError.code === '23505' || profileError.message?.includes('duplicate key')) {
          console.log('✅ Profile already exists, fetching existing profile');

          // Try to get the existing profile
          const { data: existingProfile } = await client
            .from('profiles')
            .select('id')
            .eq('email', firebaseUser.email)
            .single();

          if (existingProfile) {
            return existingProfile.id;
          }
        }

        console.error('Error creating profile:', profileError);
        throw profileError;
      }

      // Create mapping entry with conflict handling
      const { error: mappingError } = await client
        .from('profile_mappings')
        .insert({
          firebase_uid: firebaseUser.uid,
          supabase_id: supabaseId,
          created_at: new Date().toISOString()
        });

      if (mappingError) {
        // Check if it's a duplicate entry error (PostgreSQL 23505)
        if (mappingError.code === '23505' || mappingError.message?.includes('duplicate key')) {
          console.log('✅ Profile mapping already exists (duplicate ignored)');
        } else {
          console.warn('Warning: Could not create profile mapping:', mappingError);
        }
        // Don't throw here - the profile was created successfully
      }

      console.log('✅ Created Supabase profile for Firebase user:', {
        firebaseUid: firebaseUser.uid,
        supabaseId,
        email: firebaseUser.email
      });

      return supabaseId;
    } catch (error) {
      console.error('❌ Failed to create Supabase profile:', error);
      throw error;
    }
  }

  /**
   * Get complete Supabase profile
   */
  private async getSupabaseProfile(supabaseId: string): Promise<UserProfile | null> {
    const client = supabaseClient.getClient();

    try {
      const { data, error } = await client
        .from('profiles')
        .select('*')
        .eq('id', supabaseId)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }

      if (!data) {
        return null;
      }

      // Convert Supabase profile to UserProfile format
      return this.convertSupabaseProfileToUserProfile(data);
    } catch (error) {
      console.error('Error getting Supabase profile:', error);
      return null;
    }
  }

  /**
   * Convert Supabase profile data to UserProfile format
   */
  private convertSupabaseProfileToUserProfile(data: Record<string, unknown>): UserProfile {
    const id = typeof data.id === 'string' ? data.id : '';
    const email = typeof data.email === 'string' ? data.email : '';
    const name = typeof data.name === 'string' ? data.name : typeof data.full_name === 'string' ? data.full_name : email.split('@')[0] || 'Unknown';

    // Build base profile with required fields
    const profile: UserProfile = {
      id,
      userId: id, // Alias for id for compatibility with service layer
      name,
      email,
      role: (data.role === 'admin' || data.role === 'freelancer' || data.role === 'client') ? data.role as UserRole : 'freelancer',
      brandColor: typeof data.brand_color === 'string' ? data.brand_color : '#4f46e5',
      template: (typeof data.template === 'string' && ['system', 'modern', 'classic', 'bold', 'minimalist', 'executive'].includes(data.template)) ? data.template as TemplateId : 'modern',
      subscription: {
        planId: (typeof data.subscription_plan_id === 'string' && ['free', 'basic', 'pro', 'enterprise', 'company'].includes(data.subscription_plan_id)) ? data.subscription_plan_id as 'free' | 'basic' | 'pro' | 'enterprise' | 'company' : 'free',
        status: (typeof data.subscription_status === 'string' && ['active', 'canceled', 'past_due'].includes(data.subscription_status)) ? data.subscription_status as 'active' | 'canceled' | 'past_due' : 'active',
        nextBillingDate: typeof data.next_billing_date === 'string' ? data.next_billing_date : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        billingPeriod: (typeof data.billing_period === 'string' && ['monthly', 'quarterly', 'yearly', 'custom'].includes(data.billing_period)) ? data.billing_period as 'monthly' | 'quarterly' | 'yearly' | 'custom' : 'monthly'
      },
      currency: (typeof data.currency === 'string' && ['USD', 'KES', 'EUR', 'GBP'].includes(data.currency.toUpperCase())) ? data.currency.toUpperCase() as CurrencyCode : 'USD',
      country: (typeof data.country === 'string' && data.country.length === 2) ? data.country.toUpperCase() : 'US',
      savedServices: Array.isArray(data.saved_services) ? data.saved_services as Service[] : [],
      paymentMethods: Array.isArray(data.payment_methods) ? data.payment_methods as PaymentMethod[] : [],
      joinedAt: typeof data.created_at === 'string' ? data.created_at : typeof data.joined_at === 'string' ? data.joined_at : new Date().toISOString(),
      notificationPreferences: (typeof data.notification_preferences === 'object' && data.notification_preferences !== null) ? data.notification_preferences as NotificationPreferences : {
        email: { jobUpdates: true, paymentReminders: true, clientOnboarding: true },
        whatsapp: { jobUpdates: false, paymentReminders: true, clientOnboarding: false },
        push: { jobUpdates: true, paymentReminders: true, clientOnboarding: true },
        sms: { jobUpdates: false, paymentReminders: false, clientOnboarding: false }
      }
    };

    // Add optional fields only if they exist (for exactOptionalPropertyTypes compliance)
    if (typeof data.business_name === 'string') {
      profile.businessName = data.business_name;
    } else if (typeof data.company_name === 'string') {
      profile.businessName = data.company_name;
    }

    if (typeof data.business_email === 'string') {
      profile.businessEmail = data.business_email;
    }

    if (typeof data.business_address === 'string') {
      profile.businessAddress = data.business_address;
    } else if (typeof data.address === 'string') {
      profile.businessAddress = data.address;
    }

    if (typeof data.business_phone === 'string') {
      profile.businessPhone = data.business_phone;
    } else if (typeof data.phone === 'string') {
      profile.businessPhone = data.phone;
    }

    if (typeof data.logo_url === 'string') {
      profile.logoUrl = data.logo_url;
    }

    if (typeof data.has_completed_onboarding === 'boolean') {
      profile.hasCompletedOnboarding = data.has_completed_onboarding;
    }

    return profile;
  }

  /**
   * Check if a column exists in a table (simple heuristic)
   */
  private doesColumnExist(table: string, column: string): boolean {
    // For now, assume common columns exist
    // In a real implementation, you might query the schema
    const knownColumns: Record<string, string[]> = {
      'profiles': ['id', 'email', 'name', 'full_name', 'created_at', 'updated_at', 'firebase_uid']
    };

    return knownColumns[table]?.includes(column) || false;
  }

  /**
   * Clear the profile cache (useful for testing or logout)
   */
  clearCache(): void {
    this.profileCache.clear();
  }

  /**
   * Get the current user's Supabase ID from Firebase user
   */
  async getCurrentUserSupabaseId(firebaseUser: User): Promise<string | null> {
    try {
      const profile = await this.getOrCreateSupabaseProfile(firebaseUser);
      return profile.id;
    } catch (error) {
      console.error('Error getting current user Supabase ID:', error);
      return null;
    }
  }
}

export const userProfileSyncService = UserProfileSyncService.getInstance();
export default userProfileSyncService;